'use client';

import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { generalConfig } from '@sola/config/lib/getters.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';

import { getDefaultAmount } from '../../../utils/fund.ts';
import {
  DonateAction,
  DonateActionEnum,
  DonateContextType,
  DonateFrequency,
  DonateState,
  DonateVariants,
} from './donate.types';

// Reducer
const donateReducer = (
  state: DonateState,
  action: DonateAction,
): DonateState => {
  switch (action.type) {
    case DonateActionEnum.SET_FREQUENCY:
      return {
        ...state,
        donateFrequency: action.payload,
        submitError: null,
      };

    case DonateActionEnum.SET_AMOUNT:
      return {
        ...state,
        selectedAmounts: {
          ...state.selectedAmounts,
          [action.payload.frequency]: action.payload.amount,
        },
        otherSelected: false,
        submitError: null,
      };

    case DonateActionEnum.SET_OTHER_AMOUNT:
      return {
        ...state,
        otherAmounts: {
          ...state.otherAmounts,
          [action.payload.frequency]: action.payload.amount,
        },
        otherSelected: true,
        submitError: null,
      };

    case DonateActionEnum.SET_OTHER_SELECTED:
      return {
        ...state,
        otherSelected: action.payload,
        submitError: null,
      };

    case DonateActionEnum.SET_SUBMITTING:
      return { ...state, isSubmitting: action.payload };

    case DonateActionEnum.SET_SUBMIT_ERROR:
      return { ...state, submitError: action.payload };

    default:
      return state;
  }
};

// Context
const DonateContext = createContext<DonateContextType | undefined>(undefined);

// Provider
export const DonateProvider: React.FC<
  {
    variant: DonateVariants;
    children: React.ReactNode;
  } & FlexibleContentProps
> = ({ variant = DonateVariants.default, children, ...props }) => {
  const refs = {
    [DonateFrequency.OneTime]: useRef<HTMLInputElement>(null!),
    [DonateFrequency.Monthly]: useRef<HTMLInputElement>(null!),
    [DonateFrequency.Annually]: useRef<HTMLInputElement>(null!),
  };

  const enabledFrequencies = [];
  if (
    props?.fund?.availableFrequencies?.includes(DonateFrequency.OneTime) &&
    ((props?.fund?.suggestedAmountsOneTime &&
      props.fund.suggestedAmountsOneTime.length > 0) ||
      props.fund.suggestedAmount)
  ) {
    enabledFrequencies.push(DonateFrequency.OneTime);
  }
  if (
    props?.fund?.availableFrequencies?.includes(DonateFrequency.Monthly) &&
    props?.fund?.suggestedAmountsMonthly &&
    props.fund.suggestedAmountsMonthly.length > 0
  ) {
    enabledFrequencies.push(DonateFrequency.Monthly);
  }
  if (
    props?.fund?.availableFrequencies?.includes(DonateFrequency.Annually) &&
    props?.fund?.suggestedAmountsAnnually &&
    props.fund.suggestedAmountsAnnually.length > 0
  ) {
    enabledFrequencies.push(DonateFrequency.Annually);
  }

  const config = {
    donationLimits: {
      min: props?.fund?.minOneTimeAmount || generalConfig.donation.amountMin,
      max: props?.fund?.donationLimitTotal || generalConfig.donation.amountMax,
    },
    enabledFrequencies: enabledFrequencies as DonateFrequency[],
    defaultFrequency:
      (enabledFrequencies?.[0] as DonateFrequency) || DonateFrequency.OneTime,
  };

  const stateVariants = {
    default: {
      // default "selected" to the suggested amounts on the fund, or the second option in the list as a default
      selectedAmounts: {
        [DonateFrequency.OneTime]: getDefaultAmount(
          props.fund?.suggestedAmountsOneTime,
          'other',
        ),
        [DonateFrequency.Monthly]: getDefaultAmount(
          props.fund?.suggestedAmountsMonthly,
          'other',
        ),
        [DonateFrequency.Annually]: getDefaultAmount(
          props.fund?.suggestedAmountsAnnually,
          'other',
        ),
      },
      otherAmounts: {
        [DonateFrequency.OneTime]:
          props.fund?.suggestedAmount?.toString() || '',
        [DonateFrequency.Monthly]:
          props.fund?.suggestedAmount?.toString() || '',
        [DonateFrequency.Annually]:
          props.fund?.suggestedAmount?.toString() || '',
      },
      otherSelected:
        !props.fund?.[`suggestedAmounts${config.defaultFrequency}`]?.length,
    },
    grid: {
      selectedAmounts: {
        [DonateFrequency.OneTime]: 'other',
        [DonateFrequency.Monthly]: 'other',
        [DonateFrequency.Annually]: 'other',
      },
      otherAmounts: {
        [DonateFrequency.OneTime]: getDefaultAmount(
          props.fund?.suggestedAmountsOneTime,
          '',
        ),
        [DonateFrequency.Monthly]: getDefaultAmount(
          props.fund?.suggestedAmountsMonthly,
          '',
        ),
        [DonateFrequency.Annually]: getDefaultAmount(
          props.fund?.suggestedAmountsAnnually,
          '',
        ),
      },
      otherSelected: true,
    },
  };

  const initialState: DonateState = {
    donateFrequency: config.defaultFrequency,
    ...stateVariants[variant],
    isSubmitting: false,
    submitError: null,
  };
  const [state, dispatch] = useReducer(donateReducer, initialState);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      refs,
      props,
      variant,
      config,
    }),
    [state, dispatch, props, variant, config],
  );

  return (
    <DonateContext.Provider value={contextValue}>
      {children}
    </DonateContext.Provider>
  );
};

// Hook
export const useDonate = () => {
  const context = useContext(DonateContext);
  if (context === undefined) {
    throw new Error('useDonate must be used within a DonateProvider');
  }
  return context;
};
